"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Mail } from "lucide-react";
import { SiLine } from "react-icons/si";
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";

type Provider = "google" | "microsoft" | "line";

type ApiError =
  | { code?: string; detail?: string; email?: string[] }
  | Record<string, never>;

function isApiError(x: unknown): x is ApiError {
  return typeof x === "object" && x !== null;
}

const envOn = (v: string | undefined | null) => v === "1";

export default function SignupPage() {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [socialPending, setSocialPending] = useState<null | Provider>(null);

  const USE_AUTH_HUB = useMemo(() => envOn(process.env.NEXT_PUBLIC_USE_AUTH_HUB), []);
  const ENABLE_LINE = useMemo(() => envOn(process.env.NEXT_PUBLIC_ENABLE_LINE), []);
  const ENABLE_GOOGLE = useMemo(() => envOn(process.env.NEXT_PUBLIC_ENABLE_GOOGLE), []);
  const ENABLE_MS = useMemo(() => envOn(process.env.NEXT_PUBLIC_ENABLE_MS), []);
  const COMPLETE_PATH =
    process.env.NEXT_PUBLIC_SIGNUP_COMPLETE_PATH || "/signup/complete";

  const HAS_SOCIAL = ENABLE_LINE || ENABLE_GOOGLE || ENABLE_MS;

  const handleSignup = async (): Promise<void> => {
    setError("");
    setOkMsg("");

    if (!email) {
      setError("メールアドレスを入力してください。");
      return;
    }

    setPending(true);
    try {
      const res = await fetch("/api/v1/auth/signup/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        let payload: unknown = null;
        try {
          const text = await res.text();
          payload = text ? JSON.parse(text) : null;
        } catch {
          // noop
        }

        const code = isApiError(payload) ? payload.code : undefined;
        const detail = isApiError(payload) ? payload.detail : undefined;
        const emailErr =
          isApiError(payload) && Array.isArray(payload.email)
            ? payload.email[0]
            : undefined;

        if (res.status === 409 || code === "already_activated") {
          setError("このメールアドレスは既に登録されています。ログインをご利用ください。");
        } else if (res.status === 429 || code === "rate_limited") {
          setError("短時間に多数のリクエストが行われました。しばらく待ってからお試しください。");
        } else if (res.status === 422) {
          setError(emailErr ?? detail ?? "入力内容をご確認ください。");
        } else if (res.status === 503 || code === "email_send_failed") {
          setError("メール送信に失敗しました。しばらくしてから再度お試しください。");
        } else {
          setError("登録に失敗しました。時間をおいてお試しください。");
        }
        return;
      }

      setOkMsg("認証メールを送信しました。受信ボックスをご確認ください。");
    } catch {
      setError("通信エラーが発生しました。ネットワーク環境をご確認ください。");
    } finally {
      setPending(false);
    }
  };

  const startOidc = async (provider: Provider): Promise<void> => {
    setError("");
    setSocialPending(provider);

    try {
      const origin = window.location.origin;

      if (USE_AUTH_HUB) {
        const u = new URL("/auth/oidc/login", origin);
        u.searchParams.set("provider", provider);
        u.searchParams.set("next", COMPLETE_PATH);
        window.location.href = u.toString();
        return;
      }

      const redirect_uri = `${origin}/api/v1/auth/login/${provider}/callback-cookie`;
      const res = await fetch(`/api/v1/auth/login/${provider}/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ redirect_uri, next: COMPLETE_PATH }),
      });

      const raw: unknown = await res.json().catch(() => ({}));
      const obj = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
      const redirect =
        typeof obj["redirect"] === "string"
          ? obj["redirect"]
          : typeof obj["url"] === "string"
            ? obj["url"]
            : undefined;

      if (!res.ok || !redirect) {
        const detail = typeof obj["detail"] === "string" ? obj["detail"] : undefined;
        throw new Error(detail ?? "外部ログインの開始に失敗しました。");
      }

      window.location.href = redirect;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "外部ログインの開始に失敗しました。");
      setSocialPending(null);
    }
  };

  return (
    <div
      className="
        relative flex min-h-screen items-center justify-center
        bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300
        dark:from-slate-950 dark:via-slate-900 dark:to-black
        px-4 py-12
      "
    >
      <div className="w-full max-w-md">
        <div
          className="
            overflow-hidden rounded-2xl
            bg-white/70 shadow-2xl ring-1 ring-black/10 backdrop-blur-lg
            dark:bg-slate-900/80 dark:ring-slate-700
          "
        >
          <div className="flex justify-center pt-8 pb-4">
            <Image src="/logo.png" alt="OCR-Works Logo" width={180} height={40} priority />
          </div>

          <div className="px-8 pb-10">
            <h1 className="mb-6 text-center text-md text-gray-700 dark:text-slate-100">
              サインアップ
            </h1>
            <p className="text-center text-sm text-gray-500">
              メール認証リンクをお送りします。リンクを開くと登録が完了します。
            </p>

            <p className="mt-3 text-right text-sm text-gray-400">
              既にアカウントをお持ちの方は{" "}
              <Link href="/login" className="underline">
                こちら（ログイン）
              </Link>
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleSignup();
              }}
              className="mt-6 space-y-4"
            >
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="メールアドレス"
                  autoComplete="email"
                  className="
                    w-full rounded-lg border border-indigo-200 bg-white/70 px-10 py-3 text-sm
                    text-gray-900 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-indigo-400
                    dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-100 dark:placeholder-slate-500
                  "
                  required
                  aria-busy={pending}
                />
              </div>

              <button
                type="submit"
                disabled={pending}
                className="
                  w-full rounded-lg bg-gradient-to-r from-sky-800 to-sky-900
                  py-3 text-sm font-semibold text-white
                  transition hover:brightness-110 active:scale-95 disabled:opacity-60
                "
                aria-busy={pending}
              >
                {pending ? "送信中..." : "認証メールを送信"}
              </button>
            </form>

            {HAS_SOCIAL && (
              <>
                <div className="my-6 text-center text-sm text-gray-500">または</div>

                <div className="space-y-2">
                  {ENABLE_LINE && (
                    <>
                      <button
                        type="button"
                        onClick={() => void startOidc("line")}
                        disabled={socialPending === "line"}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-500 py-3 text-sm font-semibold text-white transition hover:bg-green-600 active:scale-95 disabled:opacity-60"
                        aria-busy={socialPending === "line"}
                      >
                        <SiLine className="h-5 w-5" />
                        {socialPending === "line" ? "LINEにリダイレクト中..." : "LINEで登録・ログイン"}
                      </button>
                      <p className="mt-2 text-center text-xs text-gray-500 dark:text-slate-400">
                        LINEでアカウント登録できます。案件打診をLINEで受け取るには、
                        登録後に公式アカウントの友だち追加が必要です。
                      </p>
                    </>
                  )}

                  {ENABLE_GOOGLE && (
                    <button
                      type="button"
                      onClick={() => void startOidc("google")}
                      disabled={socialPending === "google"}
                      className="
                        flex w-full items-center justify-center gap-2
                        rounded-lg border border-gray-200 bg-white py-3 text-sm font-semibold
                        text-gray-800 transition hover:bg-gray-50 active:scale-95 disabled:opacity-60
                        dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800
                      "
                      aria-busy={socialPending === "google"}
                    >
                      <FcGoogle className="h-5 w-5" />
                      {socialPending === "google" ? "Googleにリダイレクト中..." : "Googleで登録・ログイン"}
                    </button>
                  )}

                  {ENABLE_MS && (
                    <button
                      type="button"
                      onClick={() => void startOidc("microsoft")}
                      disabled={socialPending === "microsoft"}
                      className="
                        flex w-full items-center justify-center gap-2
                        rounded-lg border border-gray-200 bg-white py-3 text-sm font-semibold
                        text-gray-800 transition hover:bg-gray-50 active:scale-95 disabled:opacity-60
                        dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800
                      "
                      aria-busy={socialPending === "microsoft"}
                    >
                      <FaMicrosoft className="h-5 w-5" />
                      {socialPending === "microsoft" ? "Microsoftにリダイレクト中..." : "Microsoftで登録・ログイン"}
                    </button>
                  )}
                </div>
              </>
            )}

            {okMsg && (
              <p className="mt-6 rounded-lg bg-green-50 p-3 text-center text-sm text-green-700 ring-1 ring-green-200">
                {okMsg}
              </p>
            )}

            {error && <p className="mt-4 text-center text-sm text-red-600">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}